import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { ChevronRight, Calendar, BookOpen, ShoppingCart, Heart, TrendingUp, Target, Clock } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { getPublicPageTopPadding, getStandardTopPadding } from '@/lib/layout-utils';
import { useUser } from '@/contexts/UserContext';

const HowItWorks = () => {
  const { isAuthenticated } = useUser();
  const topPadding = isAuthenticated ? getStandardTopPadding() : getPublicPageTopPadding();
  
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to use forkast for AI-powered family meal planning",
    "description": "Learn how to use forkast's AI-powered meal planning in three simple steps to organize your family's weekly dinners and reduce meal planning stress.",
    "step": [{
      "@type": "HowToStep",
      "name": "Build your recipe collection",
      "text": "Import recipes from any website or add your family favorites to create your personalized recipe collection for AI analysis."
    }, {
      "@type": "HowToStep",
      "name": "Plan together with AI suggestions",
      "text": "Use the shared calendar to plan lunch and dinner with your family members, enhanced with AI-powered meal recommendations."
    }, {
      "@type": "HowToStep",
      "name": "Shop smart",
      "text": "Generate organized shopping lists from your planned meals, categorized by food type with intelligent ingredient optimization."
    }]
  };

  return (
    <div className={`min-h-screen bg-background ${topPadding}`}>
      <SEOHead 
        title="How it works - AI-powered family meal planning" 
        description="Learn how forkast's AI-powered meal planning works: build your recipe collection, plan meals with family, and generate intelligent shopping lists. Experience the future of family meal organization." 
        jsonLd={jsonLd} 
      />
      
      {/* How It Works section */}
      <section className="py-6 md:py-20 bg-gradient-to-br from-background via-cream/10 to-sage/5">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h1 className="text-3xl md:text-4xl font-bold font-playfair text-charcoal mb-4">
              How forkast works
            </h1>
            <p className="text-muted-foreground font-inter max-w-2xl mx-auto text-lg">
              Simple meal planning hub for busy families, with AI-powered features
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
            {/* Step 1 */}
            <div className="flex items-start gap-4 bg-background/60 rounded-xl p-6 shadow-sm border border-sage/10">
              <div className="flex-shrink-0 w-10 h-10 bg-sage text-cream rounded-full flex items-center justify-center font-bold text-lg">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold font-playfair text-charcoal mb-2 inline">
                  Build your collection
                </h3>
                <p className="text-muted-foreground font-inter">
                  Import recipes from any website or add your family favorites to create your personalized recipe collection.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-4 bg-background/60 rounded-xl p-6 shadow-sm border border-terracotta/10">
              <div className="flex-shrink-0 w-10 h-10 bg-terracotta text-cream rounded-full flex items-center justify-center font-bold text-lg">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold font-playfair text-charcoal mb-2 inline">
                  Plan together
                </h3>
                <p className="text-muted-foreground font-inter">
                  Use the shared calendar to plan breakfast, lunch and dinner with your family members, enhanced with AI-powered meal recommendations.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-4 bg-background/60 rounded-xl p-6 shadow-sm border border-golden/20">
              <div className="flex-shrink-0 w-10 h-10 bg-golden-dark text-cream rounded-full flex items-center justify-center font-bold text-lg">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold font-playfair text-charcoal mb-2 inline">
                  Shop smart
                </h3>
                <p className="text-muted-foreground font-inter">
                  Generate organized shopping lists from your planned meals, categorized by food type with intelligent ingredient optimization.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signup CTA */}
      <section className="py-2 bg-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth/register">
              <Button size="lg" className="px-8 py-4 bg-sunset hover:bg-sunset-dark text-white font-inter font-medium">
                Signup for free
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/meal-planning-tips">
              <Button variant="outline" size="lg" className="px-8 py-4 border-sunset text-sunset hover:bg-sunset hover:text-white font-inter font-medium">
                Meal planning tips
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* New user journey and AI features */}
      <section className="py-8 md:py-12 bg-gradient-to-br from-background via-cream/20 to-sage/5">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-playfair text-charcoal mb-4">
              Your personalized meal planning journey
            </h2>
            <p className="text-muted-foreground font-inter max-w-2xl mx-auto">
              From setup to smart suggestions - see how forkast guides new users and unlocks 
              AI-powered features as you build your meal planning habits
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            {/* Getting started column */}
            <div className="bg-background/80 rounded-2xl p-6 md:p-8 border border-sage/20 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Target className="h-6 w-6 text-sage" />
                <h3 className="text-2xl font-bold font-playfair text-charcoal">
                  Getting started
                </h3>
              </div>
              
              <p className="text-muted-foreground font-inter mb-6">
                Our guided onboarding helps you set up your meal planning foundation in just 
                three simple steps. Track your progress and see immediate value from day one.
              </p>
              
              <div className="rounded-lg overflow-hidden border-2 border-sage/20 shadow-lg bg-white p-2">
                <img 
                  src="/lovable-uploads/7d87d4c5-4de2-4e42-bbcc-f34d9f720014.png" 
                  alt="Forkast onboarding steps showing progress through adding recipes, planning meals, and creating shopping lists"
                  className="w-full h-auto rounded border border-gray-200"
                />
              </div>
            </div>

            {/* What you'll unlock column */}
            <div className="bg-background/80 rounded-2xl p-6 md:p-8 border border-terracotta/20 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="h-6 w-6 text-terracotta" />
                <h3 className="text-2xl font-bold font-playfair text-charcoal">
                  What you'll unlock
                </h3>
              </div>
              
              <p className="text-muted-foreground font-inter mb-6">
                As you use forkast, our AI learns your preferences to provide personalized 
                dinner suggestions and valuable insights about your family's eating patterns.
              </p>
              
              <div className="space-y-4">
                <div className="rounded-lg overflow-hidden border-2 border-terracotta/20 shadow-lg bg-white p-2">
                  <img 
                    src="/lovable-uploads/bd45358c-e488-45ad-887d-3be1959225c2.png" 
                    alt="AI-powered dinner suggestions with personalized recipe recommendations"
                    className="w-full h-auto rounded border border-gray-200"
                  />
                </div>
              </div>
            </div>

            {/* Wellness insights column */}
            <div className="bg-background/80 rounded-2xl p-6 md:p-8 border border-golden/20 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="h-6 w-6 text-golden-dark" />
                <h3 className="text-2xl font-bold font-playfair text-charcoal">
                  Wellness insights
                </h3>
              </div>
              
              <p className="text-muted-foreground font-inter mb-6">
                Track your family's nutritional wellness with personalized scores and insights. 
                Get tailored suggestions for improving fiber intake, vegetable diversity, and meal balance.
              </p>
              
              <div className="rounded-lg overflow-hidden border-2 border-golden/20 shadow-lg bg-white p-2">
                <img 
                  src="/lovable-uploads/129a427e-0847-4a73-9cb3-25833fca2d1e.png" 
                  alt="Wellness dashboard showing nutritional insights with fiber tracking and vegetable diversity metrics"
                  className="w-full h-auto rounded border border-gray-200"
                />
              </div>
            </div>

            {/* Overview experience column */}
            <div className="bg-background/80 rounded-2xl p-6 md:p-8 border border-sunset/20 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="h-6 w-6 text-sunset" />
                <h3 className="text-2xl font-bold font-playfair text-charcoal">
                  Personalized overview
                </h3>
              </div>
              
              <p className="text-muted-foreground font-inter mb-6">
                Your personalized dashboard adapts to your family's routine, showing upcoming meals, 
                recent activity, and quick planning options tailored to your preferences.
              </p>
              
              <div className="rounded-lg overflow-hidden border-2 border-sunset/20 shadow-lg bg-white p-2">
                <img 
                  src="/lovable-uploads/37d9bd48-0470-4eaf-b76f-a849d1968832.png" 
                  alt="Personalized overview dashboard showing upcoming meals and recent family activity"
                  className="w-full h-auto rounded border border-gray-200"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration CTA */}
      <section className="py-2 bg-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center">
            <Link to="/auth/register">
              <Button size="lg" className="px-8 py-4 bg-sunset hover:bg-sunset-dark text-white font-inter font-medium">
                Start planning your meals NOW →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Packed with features section */}
      <section className="py-8 md:py-12 bg-gradient-to-br from-cream/20 via-background to-golden/5">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-playfair text-charcoal mb-4">
              Packed with features
            </h2>
            <p className="text-muted-foreground font-inter max-w-2xl mx-auto">
              Discover the comprehensive set of tools designed to make meal planning 
              effortless and enjoyable for your entire family
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Meal Planning Feature */}
            <div className="bg-background/80 rounded-2xl p-6 md:p-8 border border-golden/20 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <Calendar className="h-6 w-6 text-golden-dark" />
                <h3 className="text-xl font-bold font-playfair text-charcoal">
                  Smart meal organization
                </h3>
              </div>
              <p className="text-muted-foreground font-inter mb-6">
                Plan multiple meals per day with our intuitive interface. Easily manage lunch and dinner 
                options while keeping track of who's planning what for each day of the week.
              </p>
              <div className="rounded-lg overflow-hidden border-2 border-golden/20 shadow-lg bg-white p-2">
                <img 
                  src="/lovable-uploads/daf770a1-108a-40bd-959b-1bcdfb5ab79f.png" 
                  alt="Meal planning interface showing daily meal organization with lunch and dinner options"
                  className="w-full h-auto rounded border border-gray-200"
                />
              </div>
            </div>

            {/* Reviews & Comments Feature */}
            <div className="bg-background/80 rounded-2xl p-6 md:p-8 border border-sage/20 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <Heart className="h-6 w-6 text-sage" />
                <h3 className="text-xl font-bold font-playfair text-charcoal">
                  Family feedback system
                </h3>
              </div>
              <p className="text-muted-foreground font-inter mb-6">
                Rate and review recipes with your family members. Share cooking tips, 
                modifications, and keep track of everyone's favorites with our built-in rating system.
              </p>
              <div className="rounded-lg overflow-hidden border-2 border-sage/20 shadow-lg bg-white p-2">
                <img 
                  src="/lovable-uploads/09c58443-d9b0-4aab-af02-f07f52deb61f.png" 
                  alt="Recipe reviews and comments section with star ratings and family feedback"
                  className="w-full h-auto rounded border border-gray-200"
                />
              </div>
            </div>

            {/* Recipe Details Feature */}
            <div className="bg-background/80 rounded-2xl p-6 md:p-8 border border-terracotta/20 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <BookOpen className="h-6 w-6 text-terracotta" />
                <h3 className="text-xl font-bold font-playfair text-charcoal">
                  Detailed recipe management
                </h3>
              </div>
              <p className="text-muted-foreground font-inter mb-6">
                Store complete recipes with precise ingredients and step-by-step instructions. 
                Everything you need to recreate your family's favorite meals is organized and accessible.
              </p>
              <div className="rounded-lg overflow-hidden border-2 border-terracotta/20 shadow-lg bg-white p-2">
                <img 
                  src="/lovable-uploads/483eff16-8e7c-456b-b51a-f92cb58df880.png" 
                  alt="Recipe details view showing ingredients list and cooking instructions"
                  className="w-full h-auto rounded border border-gray-200"
                />
              </div>
            </div>

            {/* Monthly Meal View Feature */}
            <div className="bg-background/80 rounded-2xl p-6 md:p-8 border border-sunset/20 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="h-6 w-6 text-sunset" />
                <h3 className="text-xl font-bold font-playfair text-charcoal">
                  Monthly meal view
                </h3>
              </div>
              <p className="text-muted-foreground font-inter mb-6">
                Look back at your meal history to discover patterns, identify favorite dishes, and get inspired 
                for future planning. See what worked well and plan ahead with confidence.
              </p>
              <div className="rounded-lg overflow-hidden border-2 border-sunset/20 shadow-lg bg-white p-2">
                <img 
                  src="/lovable-uploads/3c6cbf45-113f-460a-951d-6f218dadd939.png" 
                  alt="Monthly meal view interface showing meal history and patterns for better future planning"
                  className="w-full h-auto rounded border border-gray-200"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Us section */}
      <section className="py-8 md:py-12 pb-28 md:pb-12 bg-background">
        <div className="container px-4 md:px-6 mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Heart className="h-6 w-6 text-terracotta" />
              <Badge variant="secondary" className="bg-terracotta/20 text-terracotta font-inter">
                Beta
              </Badge>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold font-playfair text-charcoal mb-6">
              Built by busy parents
            </h2>
            
            <div className="space-y-4 mb-8">
              <p className="text-xl text-muted-foreground font-inter">
                Currently in beta. Designed to evolve with your feedback.
              </p>
              <p className="text-lg text-muted-foreground font-inter">
                We're just getting started — and we'd love your input.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth/register">
                <Button size="lg" className="px-8 py-4 bg-sunset hover:bg-sunset-dark text-white font-inter font-medium">
                  Signup for free
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="https://tally.so/r/waqWQX" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="lg" className="px-8 py-4 border-sunset text-sunset hover:bg-sunset hover:text-white font-inter font-medium">
                  Share feedback
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;